import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Send,
  Trash2,
  AlertTriangle,
  FileText,
  Download,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EditPR = () => {
  const { prId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    summary: "",
    body: "",
    topic: "",
    city: "",
    country: "",
    company_boilerplate: "",
    video_url: "",
    publish_timing: "asap",
    scheduled_date: "",
    white_label: false,
    public_org: "",
    public_website: "",
    public_name: "",
    public_email: "",
    public_phone: "",
    public_address: "",
    internal_org: "",
    internal_website: "",
    internal_name: "",
    internal_email: "",
    internal_phone: "",
    internal_address: "",
    status: "draft",
    rejection_reason: "",
    report_file: null,
    report_uploaded_at: null,
  });

  const handleDownloadReport = () => {
    if (formData?.report_file) {
      window.open(`${API_URL}/api/reports/${formData.report_file}`, "_blank");
    }
  };

  const topics = [
    "Business", "Technology", "Finance", "Healthcare", "Entertainment",
    "Sports", "Education", "Real Estate", "Automotive", "Travel",
    "Food & Beverage", "Fashion", "Energy", "Environment", "Legal", "Other",
  ];

  useEffect(() => {
    fetchData();
  }, [prId]);

  const fetchData = async () => {
    try {
      const [prRes, channelsRes] = await Promise.all([
        fetch(`${API_URL}/api/press-releases/${prId}`, { credentials: "include" }),
        fetch(`${API_URL}/api/distribution-channels`, { credentials: "include" }),
      ]);

      if (prRes.ok) {
        const pr = await prRes.json();
        setFormData(pr);
        setSelectedChannels(pr.distribution_channels || []);
      } else {
        toast.error("Press release not found");
        navigate("/manage-pr");
      }

      if (channelsRes.ok) {
        setChannels(await channelsRes.json());
      }
    } catch (error) {
      toast.error("Failed to load press release");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleChannelToggle = (channelId) => {
    setSelectedChannels((prev) =>
      prev.includes(channelId)
        ? prev.filter((id) => id !== channelId)
        : [...prev, channelId]
    );
  };

  const handleSave = async (newStatus = null) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/press-releases/${prId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          distribution_channels: selectedChannels,
          status: newStatus || formData.status,
        }),
      });

      if (response.ok) {
        toast.success(newStatus === "pending" ? "Submitted for review" : "Changes saved");
        if (newStatus === "pending") {
          navigate("/manage-pr");
        }
      } else {
        toast.error("Failed to save");
      }
    } catch (error) {
      toast.error("Error saving");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this press release?")) return;

    try {
      const response = await fetch(`${API_URL}/api/press-releases/${prId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Press release deleted");
        navigate("/manage-pr");
      } else {
        toast.error("Failed to delete");
      }
    } catch (error) {
      toast.error("Error deleting");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-700",
      pending: "bg-amber-50 text-amber-700",
      scheduled: "bg-blue-50 text-blue-700",
      published: "bg-emerald-50 text-emerald-700",
      rejected: "bg-red-50 text-red-700",
      needs_revision: "bg-orange-50 text-orange-700",
    };
    return styles[status] || styles.draft;
  };

  const canEdit = ["draft", "rejected", "needs_revision"].includes(formData.status);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/manage-pr")}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-heading font-bold text-slate-900">
                PR #{prId}
              </h1>
              <Badge className={`${getStatusBadge(formData.status)} capitalize`}>
                {formData.status?.replace("_", " ")}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {canEdit && (
            <>
              <Button
                variant="outline"
                onClick={handleDelete}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                data-testid="delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSave()}
                disabled={saving}
                data-testid="save-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button
                onClick={() => handleSave("pending")}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Submit for Review
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Rejection Notice */}
      {(formData.status === "rejected" || formData.status === "needs_revision") &&
        formData.rejection_reason && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-red-700">Revision Required</p>
                  <p className="text-sm text-red-600 mt-1">{formData.rejection_reason}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      {/* Distribution Report - Show if available */}
      {formData.report_file && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              Distribution Report Available
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-700">Your distribution report is ready for download.</p>
                {formData.report_uploaded_at && (
                  <p className="text-sm text-slate-500 mt-1">
                    Uploaded on {new Date(formData.report_uploaded_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <Button
                onClick={handleDownloadReport}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="download-report-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Report
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Distribution Channels */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Distribution Channels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {channels.map((channel) => (
              <div
                key={channel.channel_id}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  !canEdit ? "opacity-60" : "cursor-pointer"
                } ${
                  selectedChannels.includes(channel.channel_id)
                    ? "border-blue-300 bg-blue-50"
                    : "border-slate-200"
                }`}
                onClick={() => canEdit && handleChannelToggle(channel.channel_id)}
              >
                <Checkbox
                  checked={selectedChannels.includes(channel.channel_id)}
                  disabled={!canEdit}
                />
                <span className="text-lg">{channel.icon}</span>
                <span className="text-sm font-medium text-slate-700">{channel.name}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <Label>Publication Timing</Label>
              <RadioGroup
                value={formData.publish_timing}
                onValueChange={(v) => canEdit && handleChange("publish_timing", v)}
                className="mt-2 space-y-2"
                disabled={!canEdit}
              >
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="asap" id="asap" disabled={!canEdit} />
                  <Label htmlFor="asap" className="font-normal">
                    Publish as soon as possible (ASAP)
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="scheduled" id="scheduled" disabled={!canEdit} />
                  <Label htmlFor="scheduled" className="font-normal">
                    Schedule for later
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={formData.white_label}
                onCheckedChange={(v) => canEdit && handleChange("white_label", v)}
                disabled={!canEdit}
              />
              <Label>White-label distribution</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Press Release Details */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Press Release Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label>Topic</Label>
            <Select
              value={formData.topic}
              onValueChange={(v) => handleChange("topic", v)}
              disabled={!canEdit}
            >
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select a topic" />
              </SelectTrigger>
              <SelectContent>
                {topics.map((topic) => (
                  <SelectItem key={topic} value={topic}>
                    {topic}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Headline</Label>
            <Input
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              disabled={!canEdit}
              className="mt-2"
            />
          </div>

          <div>
            <Label>Summary</Label>
            <Textarea
              value={formData.summary}
              onChange={(e) => handleChange("summary", e.target.value)}
              disabled={!canEdit}
              className="mt-2 min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>City</Label>
              <Input
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                disabled={!canEdit}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Input
                value={formData.country}
                onChange={(e) => handleChange("country", e.target.value)}
                disabled={!canEdit}
                className="mt-2"
              />
            </div>
          </div>

          <div>
            <Label>Body</Label>
            <Textarea
              value={formData.body}
              onChange={(e) => handleChange("body", e.target.value)}
              disabled={!canEdit}
              className="mt-2 min-h-[300px]"
            />
          </div>

          <div>
            <Label>Company Boilerplate</Label>
            <Textarea
              value={formData.company_boilerplate}
              onChange={(e) => handleChange("company_boilerplate", e.target.value)}
              disabled={!canEdit}
              className="mt-2 min-h-[100px]"
            />
          </div>

          <div>
            <Label>Video URL</Label>
            <Input
              value={formData.video_url}
              onChange={(e) => handleChange("video_url", e.target.value)}
              disabled={!canEdit}
              className="mt-2"
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Public Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Organization</Label>
              <Input
                value={formData.public_org}
                onChange={(e) => handleChange("public_org", e.target.value)}
                disabled={!canEdit}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Website</Label>
              <Input
                value={formData.public_website}
                onChange={(e) => handleChange("public_website", e.target.value)}
                disabled={!canEdit}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input
                value={formData.public_name}
                onChange={(e) => handleChange("public_name", e.target.value)}
                disabled={!canEdit}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={formData.public_email}
                onChange={(e) => handleChange("public_email", e.target.value)}
                disabled={!canEdit}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.public_phone}
                onChange={(e) => handleChange("public_phone", e.target.value)}
                disabled={!canEdit}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={formData.public_address}
                onChange={(e) => handleChange("public_address", e.target.value)}
                disabled={!canEdit}
                className="mt-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditPR;
